d:\code2\matrixtech-web\src\views\productMgt\appStoreMgt\components\categoryMgt.vue
<template>
  <ComDialog
    :isShow="dialogVisible"
    :width="600"
    title="分类管理"
    alignCenter
    @close="handleClose"
  >
    <div class="category-mgt">
      <!-- 操作按钮 -->
      <div class="operation-bar">
        <el-button type="primary" size="small" @click="handleAdd" :loading="loading">新增分类</el-button>
      </div>

      <!-- 分类列表 -->
      <div class="category-table">
        <table class="el-table" size="small">
          <thead>
            <tr>
              <th style="width: 200px;">分类名</th>
              <th style="width: 100px;">排序</th>
              <th style="width: 150px;">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(category, index) in categoryList" :key="category.id || index">
              <!-- 分类名 -->
              <td>
                <div v-if="editingRowId === category.id || category.id === null">
                  <el-input v-model="category.tagName" placeholder="请输入分类名"></el-input>
                </div>
                <span v-else>{{ category.tagName }}</span>
              </td>

              <!-- 排序 -->
              <td>
                <div v-if="editingRowId === category.id || category.id === null">
                  <el-input-number v-model="category.sort" :min="1" :max="100" :precision="0"></el-input-number>
                </div>
                <span v-else>{{ category.sort }}</span>
              </td>

              <!-- 操作 -->
              <td>
                <div v-if="editingRowId === category.id || category.id === null">
                  <el-button link type="primary" size="small" @click="handleSave(category)" :loading="loading">保存</el-button>
                  <el-button link size="small" @click="handleCancel(category)">取消</el-button>
                </div>
                <div v-else>
                  <el-button link type="success" size="small" @click="handleEdit(category)">编辑</el-button>
                  <el-button link type="danger" size="small" @click="handleDelete(category)" :loading="loading">删除</el-button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <!-- 空数据提示 -->
        <div v-if="categoryList.length === 0" class="empty-tip">暂无分类数据</div>
      </div>
    </div>
  </ComDialog>
</template>

<script setup>
import { ref } from 'vue';
import ComDialog from '@/components/comDialog/index.vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '@/utils/request';
import API_CONFIG from '../api.json';

// 弹窗可见性
const dialogVisible = ref(false);

// 编辑中的行ID
const editingRowId = ref(null);

// 加载状态
const loading = ref(false);

// 分类列表数据
const categoryList = ref([]);

// 打开弹窗
const open = () => {
  dialogVisible.value = true;
  // 打开弹窗时加载分类列表
  loadCategoryList();
};

defineExpose({
  open
});

// 关闭弹窗
const handleClose = () => {
  dialogVisible.value = false;
  editingRowId.value = null;
};

// 加载分类列表
const loadCategoryList = async () => {
  try {
    loading.value = true;
    const res = await request.get(API_CONFIG.queryLabel.url);
    if (res.code === 200) {
      categoryList.value = res.data || [];
    } else {
      ElMessage.error('获取分类列表失败：' + res.msg);
    }
  } catch (error) {
    console.error('获取分类列表失败：', error);
    ElMessage.error('获取分类列表失败');
  } finally {
    loading.value = false;
  }
};

// 新增分类
const handleAdd = () => {
  // 添加新行并进入编辑态
  categoryList.value.push({
    id: null,
    name: '',
    sort: categoryList.value.length + 1
  });
  // 设置为编辑态
  editingRowId.value = null;
};

// 编辑分类
const handleEdit = (category) => {
  editingRowId.value = category.id;
};

// 保存分类
const handleSave = async (category) => {
  if (!category.tagName) {
    ElMessage.warning('请输入分类名');
    return;
  }

  try {
    loading.value = true;
    if (category.id === null) {
      // 新增
      const res = await request.post(API_CONFIG.addLabel.url, {
        tagName: category.tagName,
        sort: category.sort
      });
      if (res.code === 200) {
        ElMessage.success('分类新增成功');
        // 重新加载分类列表
        loadCategoryList();
      } else {
        ElMessage.error('分类新增失败：' + res.msg);
      }
    } else {
      // 修改
      const res = await request.put(API_CONFIG.updateLabel.url, {
        id: category.id,
        tagName: category.tagName,
        sort: category.sort
      });
      if (res.code === 200) {
        ElMessage.success('分类修改成功');
        // 重新加载分类列表
        loadCategoryList();
      } else {
        ElMessage.error('分类修改失败：' + res.msg);
      }
    }
    editingRowId.value = null;
  } catch (error) {
    console.error('保存分类失败：', error);
    ElMessage.error('保存分类失败');
  } finally {
    loading.value = false;
  }
};

// 取消编辑
const handleCancel = (category) => {
  if (category.id === null) {
    // 新增的行，直接删除
    categoryList.value.pop();
  } else {
    // 恢复原来的数据
    editingRowId.value = null;
  }
};

// 删除分类
const handleDelete = async (category) => {
  try {
    loading.value = true;
    const res = await request.delete(API_CONFIG.deleteLabel.url, {
      data: [category.id]
    });
    if (res.code === 200) {
      ElMessage.success('分类删除成功');
      // 重新加载分类列表
      loadCategoryList();
    } else {
      ElMessage.error('分类删除失败：' + res.msg);
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除分类失败：', error);
    }
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss" scoped>
.category-mgt {
  .operation-bar {
    margin-bottom: 10px;
  }

  .category-table {
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    overflow: hidden;

    table {
      width: 100%;
      border-collapse: collapse;

      th {
        background-color: #f5f7fa;
        border-bottom: 1px solid #e4e7ed;
        padding: 6px 12px;
        text-align: left;
        font-weight: 500;
      }

      td {
        border-bottom: 1px solid #e4e7ed;
        padding: 6px 12px;
        vertical-align: middle;

        .el-input,
        .el-input-number {
          width: 100%;
        }
      }

      tr:last-child td {
        border-bottom: none;
      }
    }

    .empty-tip {
      padding: 40px;
      text-align: center;
      color: #909399;
    }
  }

  .el-button {
    margin-right: 5px;
  }
}
</style>